/**
 * Estado de sesión del staff en el navegador: tokens, sede activa, superficie e
 * inactividad. Es un módulo con estado propio (no un contexto) para que `lib/api.ts`
 * pueda leerlo sin depender de React y sin ciclo de imports.
 *
 * Contrato §0/§1: todo request lleva `X-Surface` y, en rutas operativas, `X-Sede-Activa`
 * (RN-ROL-08). La expiración por inactividad de los portales es de 8 h (RN-ROL-16) y el
 * kiosko re-loguea por turno: por eso el TTL de inactividad depende de la superficie.
 */
import type { AuthUser } from "@viva/shared";
import type { Surface } from "./surface.js";
import { DEFAULT_SURFACE, idleMsForSurface } from "./surface.js";

const KEY_TOKENS = "viva.session.v1";
const KEY_BRANCH = "viva.activeBranch.v1";
const KEY_ACTIVITY = "viva.lastActivity.v1";

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

type Listener = () => void;

let session: StoredSession | null = null;
let activeBranchId: string | null = null;
let surface: Surface = DEFAULT_SURFACE;
const listeners = new Set<Listener>();

function safeStorage(): Storage | null {
  try {
    // Ventana privada, storage bloqueado o entorno de test sin DOM: la app igual arranca,
    // simplemente no recuerda la sesión.
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
}

function emit(): void {
  for (const listener of listeners) listener();
}

export function subscribeSession(listener: Listener): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function hydrateSession(): void {
  const storage = safeStorage();
  if (!storage) return;
  try {
    const raw = storage.getItem(KEY_TOKENS);
    session = raw ? (JSON.parse(raw) as StoredSession) : null;
  } catch {
    session = null;
  }
  activeBranchId = storage.getItem(KEY_BRANCH);
  if (session && isIdleExpired()) {
    // RN-ROL-16: la sesión guardada ya venció por inactividad — no se "revive".
    clearSession();
  }
}

export function getSession(): StoredSession | null {
  return session;
}

export function getAccessToken(): string | null {
  return session?.accessToken ?? null;
}

export function getRefreshToken(): string | null {
  return session?.refreshToken ?? null;
}

export function setSession(next: StoredSession): void {
  session = next;
  safeStorage()?.setItem(KEY_TOKENS, JSON.stringify(next));
  touchActivity();
  emit();
}

/** Sólo rota los tokens: el `user` no cambia en un refresh (contrato §1). */
export function setTokens(tokens: { accessToken: string; refreshToken: string }): void {
  if (!session) return;
  session = { ...session, ...tokens };
  safeStorage()?.setItem(KEY_TOKENS, JSON.stringify(session));
  emit();
}

export function setUser(user: AuthUser): void {
  if (!session) return;
  session = { ...session, user };
  safeStorage()?.setItem(KEY_TOKENS, JSON.stringify(session));
  emit();
}

export function clearSession(): void {
  session = null;
  activeBranchId = null;
  const storage = safeStorage();
  storage?.removeItem(KEY_TOKENS);
  storage?.removeItem(KEY_BRANCH);
  storage?.removeItem(KEY_ACTIVITY);
  emit();
}

// ── Sede activa (RN-ROL-08, D6) ─────────────────────────────────────────────

export function getActiveBranchId(): string | null {
  return activeBranchId;
}

export function setActiveBranchId(branchId: string | null): void {
  activeBranchId = branchId;
  const storage = safeStorage();
  if (branchId) storage?.setItem(KEY_BRANCH, branchId);
  else storage?.removeItem(KEY_BRANCH);
  emit();
}

// ── Superficie (contrato §0) ────────────────────────────────────────────────

export function getSurface(): Surface {
  return surface;
}

export function setSurface(next: Surface): void {
  if (surface === next) return;
  surface = next;
  emit();
}

// ── Inactividad (RN-ROL-16) ─────────────────────────────────────────────────

export function touchActivity(): void {
  safeStorage()?.setItem(KEY_ACTIVITY, String(Date.now()));
}

export function lastActivityAt(): number | null {
  const raw = safeStorage()?.getItem(KEY_ACTIVITY);
  const parsed = raw ? Number(raw) : NaN;
  return Number.isFinite(parsed) ? parsed : null;
}

/** `true` si pasó el TTL de inactividad de la superficie actual (RN-ROL-16). */
export function isIdleExpired(now: number = Date.now()): boolean {
  const last = lastActivityAt();
  if (last === null) return false;
  return now - last > idleMsForSurface(surface);
}

/** Sólo para tests: deja el módulo como recién cargado. */
export function resetSessionForTests(): void {
  session = null;
  activeBranchId = null;
  surface = DEFAULT_SURFACE;
  listeners.clear();
  const storage = safeStorage();
  storage?.removeItem(KEY_TOKENS);
  storage?.removeItem(KEY_BRANCH);
  storage?.removeItem(KEY_ACTIVITY);
}
